import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Edit2, AlertCircle, Shield, ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  FULL_ACCESS,
  PERMISSION_CATEGORIES,
  getPermissionLabel as getSharedPermissionLabel,
} from "@shared/permissions";

interface Role { id: number; name: string; users?: number; permissions: string[]; }

// Get all permission keys for display
function getPermissionLabel(permission: string): string {
  const shared = getSharedPermissionLabel(permission);
  if (shared !== permission) return shared;

  // Legacy fallback
  if (permission === FULL_ACCESS || permission === 'Full Access') return 'Full Access';
  return permission;
}

export function RolesTab(){
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get agency profile for ownerId
  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const r = await fetch("/api/user/profile");
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  // Use agencyId from profile (works for both brokers and agency members)
  const agencyId = (profile?.agencyId || "").toString();

  // Fetch job titles from organization
  const { data: jobTitles = [] } = useQuery({
    queryKey: ["/api/org/job-titles", "agency", agencyId],
    queryFn: async () => {
      if (!agencyId) return [];
      const r = await fetch(`/api/org/job-titles?ownerType=agency&ownerId=${agencyId}`);
      return r.ok ? r.json() : [];
    },
    enabled: !!agencyId,
  });

  // Fetch roles (permissions for job titles)
  const { data: roles = [] } = useQuery({
    queryKey: ["/api/roles"],
    queryFn: () => fetch("/api/roles").then((r) => r.json()),
  });

  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const editRoleForm = useForm<{ permissions: string[] }>({
    defaultValues: { permissions: [] },
  });

  // Create or update role for job title
  const upsertRoleMutation = useMutation({
    mutationFn: async ({ name, permissions }: { name: string; permissions: string[] }) => {
      // Check if role exists with this exact name (matching job title)
      const existingRole = roles.find((r: any) => r.name === name);
      
      if (existingRole) {
        // Update existing role
        const response = await apiRequest('PATCH', `/api/roles/${existingRole.id}`, { permissions });
        return response.json();
      } else {
        // Create new role with job title name
        const response = await apiRequest('POST', '/api/roles', { 
          name, 
          permissions,
          description: `Permissions for ${name} role` 
        });
        return response.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/roles'] });
      toast({ title: "Permissions updated", description: "Role permissions have been successfully updated.", variant: "success" });
      setEditRoleOpen(false);
      setSelectedRole(null);
    },
    onError: (error: any) => {
      console.error("Role update error:", error);
      toast({ title: "Error", description: "Failed to update permissions. Please try again.", variant: "destructive" });
    },
  });

  const handleEditRole = (data: any) => {
    if (selectedRole && selectedRole.name) {
      upsertRoleMutation.mutate({ 
        name: selectedRole.name,
        permissions: data.permissions 
      });
    }
  };

  const openEditRole = (jobTitle: any) => {
    // Find existing role that matches the job title name
    const existingRole = roles.find((r: any) => r.name === jobTitle.name);
    
    const roleData = {
      id: existingRole?.id || 0,
      name: jobTitle.name,
      permissions: Array.isArray(existingRole?.permissions) ? existingRole.permissions : [],
      users: existingRole?.users || 0,
    };
    
    setSelectedRole(roleData);
    // Reset form with current permissions
    editRoleForm.reset({ permissions: roleData.permissions });
    setEditRoleOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">User Roles & Permissions</h2>
     
        </div>
        
        {jobTitles.length === 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No job titles found. Please add job titles in the Organization tab to create roles.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {jobTitles.map((jobTitle: any) => {
          const existingRole = roles.find((r: any) => r.name === jobTitle.name);
          const permissions = Array.isArray(existingRole?.permissions) ? existingRole.permissions : [];
          // Use userCount from job title (based on employees with this title), not from role
          const userCount = jobTitle.userCount || 0;
          
          return (
            <Card key={jobTitle.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <span>{jobTitle.name}</span>
                  </CardTitle>
                  <Badge variant="secondary">{userCount} {userCount === 1 ? 'user' : 'users'}</Badge>
                </div>
             
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Permissions:</p>
                  {permissions.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {permissions.includes(FULL_ACCESS) || permissions.includes('Full Access') ? (
                        <Badge variant="default" className="text-xs bg-blue-600">
                          <Shield className="h-3 w-3 mr-1" />
                          Full Access
                        </Badge>
                      ) : (
                        permissions.slice(0, 6).map((permission: string) => (
                          <Badge key={permission} variant="outline" className="text-xs">
                            {getPermissionLabel(permission)}
                          </Badge>
                        ))
                      )}
                      {!permissions.includes(FULL_ACCESS) && !permissions.includes('Full Access') && permissions.length > 6 && (
                        <Badge variant="secondary" className="text-xs">
                          +{permissions.length - 6} more
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No permissions assigned</p>
                  )}
                </div>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => openEditRole(jobTitle)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Permissions
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Permissions - {selectedRole?.name}</DialogTitle>
            <CardDescription>
              Configure permissions for the {selectedRole?.name} role
            </CardDescription>
          </DialogHeader>
          <form onSubmit={editRoleForm.handleSubmit(handleEditRole)} className="space-y-4">
            {/* Full Access Toggle */}
            <div className="border rounded-lg p-4 bg-blue-50">
              <Controller
                control={editRoleForm.control}
                name="permissions"
                render={({ field }) => (
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="perm-full-access"
                      checked={(field.value as string[])?.includes(FULL_ACCESS) || (field.value as string[])?.includes('Full Access') || false}
                      onCheckedChange={(checked) => {
                        const currentValue = (field.value as string[]) || [];
                        if (checked) {
                          // When enabling full access, clear other permissions
                          field.onChange([FULL_ACCESS]);
                        } else {
                          // When disabling, remove full access
                          field.onChange(currentValue.filter((p) => p !== FULL_ACCESS && p !== 'Full Access'));
                        }
                      }}
                    />
                    <div>
                      <Label htmlFor="perm-full-access" className="cursor-pointer font-semibold flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-600" />
                        Full Access
                      </Label>
                      <p className="text-xs text-muted-foreground">Grants all permissions - use sparingly</p>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Permission Categories */}
            <div className="space-y-2">
              <Label className="mb-2 block">Or select specific permissions:</Label>
              <Controller
                control={editRoleForm.control}
                name="permissions"
                render={({ field }) => {
                  const currentPerms = (field.value as string[]) || [];
                  const hasFullAccess = currentPerms.includes(FULL_ACCESS) || currentPerms.includes('Full Access');

                  return (
                    <div className="space-y-2">
                      {Object.entries(PERMISSION_CATEGORIES).map(([categoryKey, category]) => {
                        const permKeys = Object.keys(category.permissions);
                        const selectedCount = hasFullAccess ? permKeys.length : permKeys.filter(p => currentPerms.includes(p)).length;
                        const allSelected = hasFullAccess || selectedCount === permKeys.length;

                        return (
                          <Collapsible key={categoryKey} className="border rounded-lg">
                            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-800">
                              <div className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-90" />
                                <span className="font-medium">{category.label}</span>
                                {selectedCount > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {selectedCount}/{permKeys.length}
                                  </Badge>
                                )}
                              </div>
                              <Checkbox
                                checked={allSelected}
                                disabled={hasFullAccess}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    // Add all permissions in category
                                    const newPerms = [...currentPerms];
                                    for (const perm of permKeys) {
                                      if (!newPerms.includes(perm)) newPerms.push(perm);
                                    }
                                    field.onChange(newPerms);
                                  } else {
                                    // Remove all permissions in category
                                    field.onChange(currentPerms.filter(p => !permKeys.includes(p)));
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="px-3 pb-3">
                              <p className="text-xs text-muted-foreground mb-2">{category.description}</p>
                              <div className="grid grid-cols-2 gap-2 pl-6">
                                {Object.entries(category.permissions).map(([permKey, permLabel]) => (
                                  <div key={permKey} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`perm-${permKey}`}
                                      checked={hasFullAccess || currentPerms.includes(permKey)}
                                      disabled={hasFullAccess}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          field.onChange([...currentPerms, permKey]);
                                        } else {
                                          field.onChange(currentPerms.filter(p => p !== permKey));
                                        }
                                      }}
                                    />
                                    <Label
                                      htmlFor={`perm-${permKey}`}
                                      className={`cursor-pointer text-sm ${hasFullAccess ? 'text-muted-foreground' : ''}`}
                                    >
                                      {permLabel}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </div>
                  );
                }}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditRoleOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Permissions</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
